import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
  vitalsCharts: [],
  currentVitalsChart: null,
  isLoading: false,
  error: null,
};

// ==================== Async Thunks ====================

// Fetch all Vitals Monitor Charts (optionally by patient ID)
export const fetchVitalsCharts = createAsyncThunk(
  'vitals/fetchVitalsCharts',
  async (patientId = null, { rejectWithValue }) => {
    try {
      const url = patientId
        ? `/vitals-monitor-charts/patient/${patientId}`
        : '/vitals-monitor-charts';
      const response = await api.get(url);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Vitals Monitor Charts'
      );
    }
  }
);

// Fetch a single Vitals Monitor Chart by ID
export const fetchVitalsChart = createAsyncThunk(
  'vitals/fetchVitalsChart',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.get(`/vitals-monitor-charts/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to fetch Vitals Monitor Chart'
      );
    }
  }
);

// Create a new Vitals Monitor Chart
export const createVitalsChart = createAsyncThunk(
  'vitals/createVitalsChart',
  async (chartData, { rejectWithValue }) => {
    try {
      const response = await api.post(`/vitals-monitor-charts`, chartData);
      return response.data.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to create Vitals Monitor Chart'
      );
    }
  }
);

// Update a Vitals Monitor Chart
export const updateVitalsChart = createAsyncThunk(
  'vitals/updateVitalsChart',
  async ({ case_id, chartData }, { rejectWithValue }) => {
    try {
      const response = await api.put(`/vitals-monitor-charts/case/${case_id}`, chartData);
      return response.data.data ?? response.data;
    } catch (error) {
      if (error.response?.status === 422) {
        return rejectWithValue(error.response.data.errors);
      }
      return rejectWithValue(
        error.response?.data?.error || 'Failed to update Vitals Monitor Chart'
      );
    }
  }
);

// Delete a Vitals Monitor Chart
export const deleteVitalsChart = createAsyncThunk(
  'vitals/deleteVitalsChart',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/vitals-monitor-charts/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.error || 'Failed to delete Vitals Monitor Chart'
      );
    }
  }
);

// ==================== Slice ====================
const vitalsSlice = createSlice({
  name: 'vitals',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentVitalsChart: (state, action) => {
      state.currentVitalsChart = action.payload;
    },
    clearCurrentVitalsChart: (state) => {
      state.currentVitalsChart = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // FETCH ALL
      .addCase(fetchVitalsCharts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVitalsCharts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vitalsCharts = action.payload;
      })
      .addCase(fetchVitalsCharts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // FETCH SINGLE
      .addCase(fetchVitalsChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchVitalsChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentVitalsChart = action.payload;
      })
      .addCase(fetchVitalsChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // CREATE
      .addCase(createVitalsChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createVitalsChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vitalsCharts.unshift(action.payload);
      })
      .addCase(createVitalsChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateVitalsChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateVitalsChart.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload.data ?? action.payload;

        const index = state.vitalsCharts.findIndex((d) => d.id === updated.id);
        if (index !== -1) state.vitalsCharts[index] = updated;

        if (state.currentVitalsChart?.id === updated.id) {
          state.currentVitalsChart = updated;
        }
      })
      .addCase(updateVitalsChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteVitalsChart.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteVitalsChart.fulfilled, (state, action) => {
        state.isLoading = false;
        state.vitalsCharts = state.vitalsCharts.filter(
          (d) => d.id !== action.payload
        );

        if (state.currentVitalsChart?.id === action.payload) {
          state.currentVitalsChart = null;
        }
      })
      .addCase(deleteVitalsChart.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setCurrentVitalsChart,
  clearCurrentVitalsChart,
} = vitalsSlice.actions;

export default vitalsSlice.reducer;
